import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNotify, useRefresh } from 'react-admin';
import { PDFFormEditor, usePDFEditor } from '../components/PDFFormEditor';
import { getToken, getRefreshToken } from '../authentication/authProvider';
import { useProofOfPaymentWarning } from './useProofOfPaymentWarning';
import { useConfirmedFormReset } from './useConfirmedFormReset';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

// Encode a Uint8Array as base64 in chunks (same helper used across the
// benefit modals) so large PDFs don't blow the String.fromCharCode arg cap.
const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
  }
  return btoa(binary);
};

export type ReviewFormKey =
  | 'cpp_death_benefit'
  | 'cpp_survivor_pension'
  | 'qpp_benefits'
  | 'qpp_proof_of_payment'
  | 'trustage_claim';

export interface BenefitFormReviewConfig {
  title: string;
  /** Backend route returning the pre-filled, form-intact PDF. */
  pdfPath: string;
  formKey: ReviewFormKey;
  /** CPP forms additionally persist the flattened bytes for DocuSeal. */
  saveFlattened: boolean;
  downloadName: string;
}

interface BenefitFormReviewModalProps {
  open: boolean;
  onClose: () => void;
  funeralId: number;
  config: BenefitFormReviewConfig | null;
  confirmed?: boolean;
  // CPP-only lifecycle props (the sidebar's CppBenefitsSection wires these; QPP
  // rows leave them undefined and the modal stays review + Ready + Download).
  // `sent` = an active signing submission exists; `signed` = a signed copy
  // exists. When `signed`, the PDF locks and Discard replaces Ready/Send.
  sent?: boolean;
  signed?: boolean;
  discarding?: boolean;
  /** Open the shared Send dialog (Send when not yet sent, Resend otherwise). */
  onSend?: () => void;
  /** Soft-archive the signed copy so the form can be corrected and resent. */
  onDiscardSigned?: () => void;
  /** Called after a successful "Ready" so the parent can refresh status. */
  onReadyChange?: () => void;
  /** Wording of the send button when it isn't a signing Send (TruStage claim: "Approve…"). */
  sendLabel?: string;
}

// Generic "review the auto-generated benefit form + mark Ready" modal used by
// the funeral sidebar's Status rows (QPP Benefits, Proof of Payment, CPP Death
// Benefit, CPP Survivor's Pension). Preview via PDFFormEditor, persist the
// agent's edits as overrides on Ready, and (for CPP) upload the flattened PDF.
// Sending for signature stays on the Survivor Benefits toolbar button; this is
// just review + Ready + Download. (OAS has its own modal because it also sends.)
const BenefitFormReviewModal: React.FC<BenefitFormReviewModalProps> = ({
  open,
  onClose,
  funeralId,
  config,
  confirmed = false,
  sent = false,
  signed = false,
  discarding = false,
  onSend,
  onDiscardSigned,
  onReadyChange,
  sendLabel,
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Whether this form participates in the Send/Resend/Discard lifecycle (CPP).
  const hasSendFlow = !!onSend;

  // Proof of Payment only: warn when payments exceed the contract total.
  const proofOfPaymentWarning = useProofOfPaymentWarning(
    funeralId,
    open && config?.formKey === 'qpp_proof_of_payment',
  );

  const { handlePdfReady, downloadFlattenedPdf, getFlattenedPdfBytes, getFieldValues } = usePDFEditor();

  // Bumped by Reset so the editor re-fetches the form, now regenerated from the case.
  const [pdfNonce, setPdfNonce] = useState(0);
  const { resetButton, driftBanner, resetDialog } = useConfirmedFormReset({
    funeralId,
    formKey: config?.formKey,
    title: config?.title,
    confirmed,
    enabled: open && !signed,
    onReset: () => {
      setLoading(true);
      setPdfNonce((n) => n + 1);
      onReadyChange?.();
    },
  });

  // One modal instance is reused across forms (the sidebar swaps `config`), so
  // reset the spinner whenever the shown form changes or the modal re-opens.
  useEffect(() => {
    if (open) setLoading(true);
  }, [open, config?.formKey]);

  const handlePdfLoaded = useCallback(
    (bytes: Uint8Array) => {
      setLoading(false);
      handlePdfReady(bytes);
    },
    [handlePdfReady],
  );

  // Persist the agent's review: store overrides and (for CPP) the flattened
  // PDF that Send uploads to DocuSeal. Returns true on success. For CPP forms
  // this runs as the first half of the Send button — there's no separate
  // "Ready" step anymore; reviewing and sending are one action.
  const handleReady = async (opts?: { silent?: boolean }): Promise<boolean> => {
    if (!config) return false;
    const overrides = getFieldValues();
    if (!overrides) {
      notify('PDF not loaded yet — wait for the preview before sending', { type: 'warning' });
      return false;
    }
    setConfirming(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await getToken()}`,
        'Refresh-Token': await getRefreshToken(),
      };
      const confirmRes = await fetch(`${PAPERMAN_API_URL}form-confirmations/${funeralId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ formKey: config.formKey, overrides }),
      });
      if (!confirmRes.ok) {
        const errBody = await confirmRes.json().catch(() => ({}));
        throw new Error(errBody?.message || `Confirm failed (${confirmRes.status})`);
      }

      if (config.saveFlattened) {
        const pdfBase64 = bytesToBase64(await getFlattenedPdfBytes());
        const saveRes = await fetch(`${PAPERMAN_API_URL}cpp-benefits/save-flattened/${funeralId}`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ formKey: config.formKey, pdfBase64 }),
        });
        if (!saveRes.ok) {
          const errBody = await saveRes.json().catch(() => ({}));
          throw new Error(errBody?.message || `Save PDF failed (${saveRes.status})`);
        }
      }

      if (!opts?.silent) notify(`${config.title} saved`, { type: 'success' });
      onReadyChange?.();
      refresh();
      return true;
    } catch (err: any) {
      console.error('Benefit form confirm error:', err);
      notify(err?.message || 'Failed to save form', { type: 'error' });
      return false;
    } finally {
      setConfirming(false);
    }
  };

  // CPP Send: persist the review (overrides + flattened PDF) then open the
  // shared Send dialog. Merges the old two-step Ready→Send into one click.
  const handleSendClick = async () => {
    const ok = await handleReady({ silent: true });
    if (ok) onSend?.();
  };

  // QPP: agents file these manually, so downloading IS completing them. Combine
  // Save + Download so a download also marks the form done (it stayed "Pending"
  // when agents downloaded without clicking Save).
  const handleSaveAndDownload = async () => {
    await handleReady({ silent: true });
    await handleDownload();
  };

  const handleDownload = async () => {
    if (!config) return;
    try {
      await downloadFlattenedPdf(config.downloadName);
      notify(`${config.title} downloaded`, { type: 'success' });
    } catch (err) {
      console.error('Benefit form download error:', err);
      notify('Failed to download form', { type: 'error' });
    }
  };

  if (!config) return null;
  const pdfUrl = `${PAPERMAN_API_URL}${config.pdfPath}/${funeralId}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiDialog-paper': { height: '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle>{config.title}</DialogTitle>
      <DialogContent sx={{ flexGrow: 1, p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            p: 1,
          }}
        >
          {resetButton}
          {/* QPP (no send flow): filing is manual, so downloading completes the
              form. One "Save & Download" button marks it done and downloads. */}
          {!hasSendFlow && !signed && (
            <Button
              onClick={handleSaveAndDownload}
              variant={confirmed ? 'outlined' : 'contained'}
              color="success"
              size="small"
              disabled={loading || confirming}
              startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            >
              Save &amp; Download
            </Button>
          )}
          {/* Standalone Download for CPP forms and locked/signed views. */}
          {(hasSendFlow || signed) && (
            <Button
              onClick={handleDownload}
              variant="outlined"
              color="primary"
              size="small"
              disabled={loading}
              startIcon={<DownloadIcon />}
            >
              Download
            </Button>
          )}
          {/* Send / Resend — CPP forms (hasSendFlow). One click saves the review
              (overrides + flattened PDF) then opens the Send dialog; there's no
              separate Ready step. Resend invalidates the previous signing link. */}
          {hasSendFlow && !signed && (
            <Button
              onClick={handleSendClick}
              variant="contained"
              color="secondary"
              size="small"
              disabled={loading || confirming}
              startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            >
              {sendLabel ?? (sent ? 'Resend' : 'Send')}
            </Button>
          )}
          {/* Discard a signed copy so the form can be corrected and resent. */}
          {hasSendFlow && signed && (
            <Button
              onClick={() => setConfirmDiscard(true)}
              variant="outlined"
              color="error"
              size="small"
              disabled={discarding}
              startIcon={discarding ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            >
              Discard signed copy
            </Button>
          )}
        </Box>

        {hasSendFlow && signed && (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            This form has been signed and is locked. To make changes, discard the
            signed copy first — then you can edit and resend it.
          </Alert>
        )}
        {hasSendFlow && sent && !signed && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            This form has already been sent. Editing and clicking{' '}
            <strong>Resend</strong> will invalidate the previous signing link.
          </Alert>
        )}

        {driftBanner}

        {proofOfPaymentWarning && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            {proofOfPaymentWarning}
          </Alert>
        )}

        <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              display: loading ? 'none' : 'block',
              height: '100%',
              pointerEvents: signed ? 'none' : 'auto',
              opacity: signed ? 0.6 : 1,
            }}
          >
            {/* key forces a fresh editor when switching which form is shown */}
            <PDFFormEditor key={`${config.formKey}-${pdfNonce}`} pdfUrl={pdfUrl} onPdfReady={handlePdfLoaded} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {resetDialog}

      <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Discard signed copy?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The signed <strong>{config.title}</strong> will be archived and removed
            from the funeral. The form returns to a sendable state so you can edit
            and resend it. The archived copy is kept in storage and can be
            recovered if needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscard(false)} disabled={discarding}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmDiscard(false);
              onDiscardSigned?.();
            }}
            variant="contained"
            color="error"
            disabled={discarding}
            startIcon={discarding ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default BenefitFormReviewModal;
